import { shallowEqual } from "react-redux";
import React from "react";
import { useUscSelector } from "../../../../../../redux/store";
import { uscNedcWltpDisclaimersLabelSelector } from "../../../../../../../shared-logic/utils/uscEcoUtils";
import { getUscEcoDropdownConfig } from "../../../../../../utils/ecoUtils";
import { UscDisclaimerType } from "../../../../../../../shared-logic/types/UscCommonTypes";
import { UscQuickSpec } from "../../../../../../../shared-logic/utils/uscUtils";
import EcoLabelImage from "../common/EcoLabelImage";
import MeasureItem from "../common/MeasureItem";
import type { SpecsSectionPropsType } from "../Specifications";
import EfficiencyClassSpec from "../../../highlights/EfficiencyClassSpec";
import List from "../../../../styles/atoms/List";
import Text from "../../../../styles/atoms/Text";
import { uscWeightedSpecsLabelSelector } from "../../../../../../utils/specUtils";

/**
 * This section contains much more than just consumption values
 * A better name would be SectionEco as this contains consumption, emissions and electric ranges
 * We don't rename this section for now as this has to many impact on the configuration
 */
// TODO check if rename is feasible
const SectionConsumption = ({ product, cultureName, specs, title }: SpecsSectionPropsType): JSX.Element | null => {
    const uscSettings = useUscSelector((state) => state.uscSettings);
    const { nedcNotAvailableDisclaimer, efficiencyClassNotAvailableDisclaimer } = useUscSelector(
        uscNedcWltpDisclaimersLabelSelector,
        shallowEqual,
    );
    const weightedSpecsLabels = useUscSelector(uscWeightedSpecsLabelSelector, shallowEqual);

    const ecoDropdownConfig = getUscEcoDropdownConfig(product, uscSettings, weightedSpecsLabels);
    const { ecoValuesPart1, ecoValuesPart2, nedcIsNotAvailable } = ecoDropdownConfig;

    // To determine if we should show this accordion, there are a few conditions:
    // - ecoValuesPart1 and ecoValuesPart2 should be populated
    // - if they are populated, we check if there are any measures in them
    // - if there are no measures, we check if there are any disclaimers

    const shouldShowAccordion =
        (ecoValuesPart1.length > 0 || ecoValuesPart2.length > 0) &&
        (ecoValuesPart1.some((item) => item.measure) ||
            ecoValuesPart2.some((item) => item.measure) ||
            nedcNotAvailableDisclaimer ||
            efficiencyClassNotAvailableDisclaimer);

    return shouldShowAccordion ? (
        <div>
            <Text tag="h4" variant="h6">
                {title}
            </Text>
            <List variant="specs" tag="dl">
                {nedcIsNotAvailable && nedcNotAvailableDisclaimer && <li>{nedcNotAvailableDisclaimer}</li>}
                {ecoValuesPart1.map(
                    ({ title: ecoValueTitle, measure, tooltipReference }, index) =>
                        measure && (
                            <MeasureItem
                                //eslint-disable-next-line react/no-array-index-key
                                key={index}
                                title={ecoValueTitle}
                                measure={measure}
                                cultureName={cultureName}
                                disclaimer={{
                                    type: UscDisclaimerType.Spec,
                                    reference: tooltipReference,
                                }}
                            />
                        ),
                )}
                {product.eco?.euroClass && specs.euroClass && (
                    <div>
                        <Text tag="dt" variant="body-book" color="grey-7">
                            {specs.euroClass.title}
                        </Text>
                        <Text tag="dd" variant="body-book">
                            {product.eco.euroClass}
                        </Text>
                    </div>
                )}

                {ecoValuesPart2.map(
                    ({ title: ecoValueTitle, measure, tooltipReference }, index) =>
                        measure && (
                            <MeasureItem
                                //eslint-disable-next-line react/no-array-index-key
                                key={index}
                                title={ecoValueTitle}
                                measure={measure}
                                cultureName={cultureName}
                                disclaimer={{
                                    type: UscDisclaimerType.Spec,
                                    reference: tooltipReference,
                                }}
                            />
                        ),
                )}

                {specs.efficiencyClass && product.eco?.efficiencyClass ? (
                    <div>
                        <Text tag="dt" variant="body-book" color="grey-7">
                            {specs.efficiencyClass.title}
                        </Text>
                        <Text tag="dd" variant="body-book">
                            <EfficiencyClassSpec
                                spec={{
                                    ...specs.efficiencyClass,
                                    type: UscQuickSpec.EfficiencyClass,
                                    value: product.eco.efficiencyClass.description,
                                    valueCode: product.eco.efficiencyClass.code,
                                }}
                            />
                        </Text>
                    </div>
                ) : specs.efficiencyClass && !product.eco?.efficiencyClass && efficiencyClassNotAvailableDisclaimer ? (
                    <div>
                        <Text tag="dt" variant="body-book" color="grey-7">
                            {specs.efficiencyClass.title}
                        </Text>
                        <Text tag="dd" variant="body-book">
                            {efficiencyClassNotAvailableDisclaimer}
                        </Text>
                    </div>
                ) : null}

                {product.eco && specs.ecoLabel && <EcoLabelImage ecoInfo={product.eco} />}
            </List>
        </div>
    ) : null;
};

export default SectionConsumption;
